import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import lang from '../../../../lib/language';
import { LiquidityAndSolvencyRatiosResponse, ProfitabilityRatiosResponse, RatiosPeriodType, ValuationRatiosResponse } from '../../../../lib/models/fundamental/types';
import { RootState } from '../../../../lib/store';
import {
  getAllRatios,
  getRatiosLiquidityAndSolvency,
  getRatiosProfitability,
  getRatiosValuation,
} from '../../../../lib/store/fundamental';

import { useIsMobile } from '../../../../hooks';
import { getLiquidityData, getProfitabilityData, getValuationData } from '../../../../util/ratios-helpers';

import RatiosComponent from './RatiosComponent/RatiosComponent';

import styles from './Ratios.module.scss';

type Props = {
    symbol: string;
};

const Ratios = (props: Props) => {
  const { symbol } = props;
  const dispatch = useDispatch();
  const { isMobile } = useIsMobile();

  const [profitabilityPeriodType, setProfitabilityPeriodType] = useState<RatiosPeriodType>(RatiosPeriodType.ANNUAL);
  const [profitabilityCurrentTimeOption, setProfitabilityCurrentTimeOption] = useState<number>(0);
  const [profitabilityTimeOptions, setProfitabilityTimeOptions] = useState<string[]>([]);
  const [profitabilityData, setProfitabilityData] = useState<Array<Array<string | number>>>([]);

  const [liquidityPeriodType, setLiquidityPeriodType] = useState<RatiosPeriodType>(RatiosPeriodType.ANNUAL);
  const [liquidityCurrentTimeOption, setLiquidityCurrentTimeOption] = useState<number>(0);
  const [liquidityTimeOptions, setLiquidityTimeOptions] = useState<string[]>([]);
  const [liquidityData, setLiquidityData] = useState<Array<Array<string | number>>>([]);

  const [valuationPeriodType, setValuationPeriodType] = useState<RatiosPeriodType>(RatiosPeriodType.ANNUAL);
  const [valuationCurrentTimeOption, setValuationCurrentTimeOption] = useState<number>(0);
  const [valuationTimeOptions, setValuationTimeOptions] = useState<string[]>([]);
  const [valuationData, setValuationData] = useState<Array<Array<string | number>>>([]);

  const profitabilityStateData = useSelector<RootState, ProfitabilityRatiosResponse>(
    (state: RootState) => state.fundamental.ratiosData.profitability,
  );

  const liquidityStateData = useSelector<RootState, LiquidityAndSolvencyRatiosResponse>(
    (state: RootState) => state.fundamental.ratiosData.liquidityAndSolvency,
  );

  const valuationStateData = useSelector<RootState, ValuationRatiosResponse>(
    (state: RootState) => state.fundamental.ratiosData.valuation,
  );

  // General call to get all ratios at loading time
  useEffect(() => {
    dispatch(getAllRatios({ symbol, periodType: RatiosPeriodType.ANNUAL }));
  }, [symbol, dispatch]);

  // Refetch profitability when period is changed
  useEffect(() => {
    dispatch(getRatiosProfitability({ symbol, periodType: profitabilityPeriodType }));
  }, [symbol, profitabilityPeriodType, dispatch]);

  // Refetch liquidity when period is changed
  useEffect(() => {
    dispatch(getRatiosLiquidityAndSolvency({ symbol, periodType: liquidityPeriodType }));
  }, [symbol, liquidityPeriodType, dispatch]);

  // Refetch valuation when period is changed
  useEffect(() => {
    dispatch(getRatiosValuation({ symbol, periodType: valuationPeriodType }));
  }, [symbol, valuationPeriodType, dispatch]);

  // Update profitability when time option is changed
  useEffect(() => {
    if ((profitabilityStateData.ratios && profitabilityStateData.ratios.length > 0)
      || (profitabilityStateData.ratios && profitabilityPeriodType === RatiosPeriodType.TTM)) {
      const data = getProfitabilityData({
        profitabilityStateData,
        profitabilityPeriodType,
        setProfitabilityTimeOptions,
        profitabilityCurrentTimeOption,
      });
      setProfitabilityData(data);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profitabilityStateData.ratios, profitabilityStateData.sectorRatios, profitabilityCurrentTimeOption]);

  // Update liquidity when time option is changed
  useEffect(() => {
    if ((liquidityStateData.ratios && liquidityStateData.ratios.length > 0)
      || (liquidityPeriodType === RatiosPeriodType.TTM)) {
      const data = getLiquidityData({
        liquidityStateData,
        liquidityPeriodType,
        setLiquidityTimeOptions,
        liquidityCurrentTimeOption,
      });
      setLiquidityData(data);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liquidityStateData.ratios, liquidityStateData.sectorRatios, liquidityCurrentTimeOption]);

  // Update valuation when time option is changed
  useEffect(() => {
    if ((valuationStateData.ratios && valuationStateData.ratios.length > 0)
      || (valuationStateData.ratios && valuationPeriodType === RatiosPeriodType.TTM)) {
      const data = getValuationData({
        valuationStateData,
        valuationPeriodType,
        setValuationTimeOptions,
        valuationCurrentTimeOption,
      });
      setValuationData(data);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuationStateData.ratios, valuationStateData.sectorRatios, valuationCurrentTimeOption]);

  const onProfitabilityPeriodTypeChange = (period: RatiosPeriodType) => {
    setProfitabilityPeriodType(period);
  };

  const onProfitabilityTimeOptionChange = (index: number) => {
    setProfitabilityCurrentTimeOption(index);
  };

  const onLiquidityPeriodTypeChange = (period: RatiosPeriodType) => {
    setLiquidityPeriodType(period);
  };

  const onLiquidityTimeOptionChange = (index: number) => {
    setLiquidityCurrentTimeOption(index);
  };

  const onValuationPeriodTypeChange = (period: RatiosPeriodType) => {
    setValuationPeriodType(period);
  };

  const onValuationTimeOptionChange = (index: number) => {
    setValuationCurrentTimeOption(index);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.column}>
        <div className={styles.component} style={{ marginTop: 0 }}>
          <RatiosComponent
            title={lang.ratiosProfitability()}
            currentPeriod={profitabilityPeriodType}
            onPeriodChange={onProfitabilityPeriodTypeChange}
            currentTimeOption={profitabilityTimeOptions[profitabilityCurrentTimeOption]}
            timeOptions={profitabilityTimeOptions}
            onTimeChange={onProfitabilityTimeOptionChange}
            data={profitabilityData}
            isLoading={profitabilityData.length === 0}
          />
        </div>
        <div className={styles.component}>
          <RatiosComponent
            title={lang.ratiosValuation()}
            currentPeriod={valuationPeriodType}
            onPeriodChange={onValuationPeriodTypeChange}
            currentTimeOption={valuationTimeOptions[valuationCurrentTimeOption]}
            timeOptions={valuationTimeOptions}
            onTimeChange={onValuationTimeOptionChange}
            data={valuationData}
            isLoading={valuationData.length === 0}
          />
        </div>
      </div>
      { isMobile && (<div className={styles.columnSpace} />)}
      <div className={styles.column}>
        <div className={styles.component}>
          <RatiosComponent
            title={lang.ratiosLiquidityAndSolvency()}
            currentPeriod={liquidityPeriodType}
            onPeriodChange={onLiquidityPeriodTypeChange}
            currentTimeOption={liquidityTimeOptions[liquidityCurrentTimeOption]}
            timeOptions={liquidityTimeOptions}
            onTimeChange={onLiquidityTimeOptionChange}
            data={liquidityData}
            isLoading={liquidityData.length === 0}
          />
        </div>
      </div>
    </div>
  );
};

export default Ratios;
